/* eslint-disable sort-imports */
/**
 * View 3 — Flex & OT requests.
 *
 * A bridge to the flex scheduler, not a replacement for it. Requests drafted on
 * the skill-gap board are listed here with their fill status; publishing hands
 * them to the real service, after which this screen reflects what that service
 * reports rather than anything held locally.
 */
import React from "react";

import { COVER_LABEL, FlexRequestRow, RequestStatus } from "../types";
import { eyebrowSx, lbb } from "../utils/tokens";

const STATUS_STYLE: Record<RequestStatus, { bg: string; fg: string }> = {
	DRAFT: { bg: lbb.alloy, fg: lbb.ink60 },
	FILLED: { bg: lbb.success, fg: lbb.paper },
	OPEN: { bg: lbb.brick, fg: lbb.paper },
};

const GRID = "220px 170px 150px 1fr 110px";

interface FlexRequestsProps {
	rows: FlexRequestRow[]
	published: boolean
	dayLabel: string
	/** Opens the real flex scheduler. */
	onOpenFlexScheduler: () => void
}

function FlexRequests({ rows, published, dayLabel, onOpenFlexScheduler }: FlexRequestsProps) {
	return (
		<div style={{ padding: 24, minWidth: 0 }}>
			<div
				style={{
					display: "flex",
					alignItems: "baseline",
					justifyContent: "space-between",
					gap: 16,
					flexWrap: "wrap",
					paddingBottom: 14,
				}}
			>
				<div style={{ ...eyebrowSx, color: lbb.brick }}>
					Flex &amp; OT requests · {dayLabel}
				</div>
				<div style={{ fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink60 }}>
					{published
						? "Live in the Wilya flex scheduler. Fill status syncs back here."
						: "Drafted from the skill-gap board. Publish to send them to the flex scheduler."}
				</div>
			</div>

			{rows.length === 0 ? (
				<div
					style={{
						padding: "48px 24px",
						textAlign: "center",
						fontFamily: lbb.fontUi,
						color: lbb.ink60,
						border: lbb.borderHair,
						borderRadius: 4,
					}}
				>
					<div style={{ fontFamily: lbb.fontBrand, fontSize: 20, fontWeight: 700, color: lbb.navy }}>
						No requests yet
					</div>
					<p style={{ fontSize: 14, marginTop: 8, lineHeight: 1.5 }}>
						Cover a gap on the skill-gap board and it will appear here, ready to publish.
					</p>
					<button
						type="button"
						onClick={onOpenFlexScheduler}
						style={{
							marginTop: 16,
							fontFamily: lbb.fontUi,
							fontSize: 13,
							fontWeight: 700,
							padding: "10px 16px",
							border: lbb.borderInk,
							borderRadius: 4,
							background: lbb.paper,
							color: lbb.navy,
							cursor: "pointer",
						}}
					>
						Open the flex scheduler ›
					</button>
				</div>
			) : (
				<>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: GRID,
							gap: 12,
							paddingBottom: 10,
							borderBottom: lbb.borderInk,
							...eyebrowSx,
							color: lbb.ink60,
						}}
					>
						<div>Work center</div>
						<div>Role</div>
						<div>Window</div>
						<div>Cover method</div>
						<div style={{ textAlign: "right" }}>Status</div>
					</div>

					{rows.map((row) => (
						<div
							key={row.key}
							style={{
								display: "grid",
								gridTemplateColumns: GRID,
								gap: 12,
								alignItems: "center",
								padding: "12px 0",
								borderBottom: lbb.borderHair,
							}}
						>
							<div
								style={{
									fontFamily: lbb.fontUi,
									fontSize: 15,
									fontWeight: 600,
									color: lbb.navy,
									overflowWrap: "anywhere",
								}}
							>
								{row.workcenter}
							</div>
							<div style={{ fontFamily: lbb.fontUi, fontSize: 14, color: lbb.navy }}>
								{row.jobRole}
							</div>
							<div style={{ fontFamily: lbb.fontMono, fontSize: 13, color: lbb.navy }}>
								{row.window}
							</div>
							<div style={{ fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink60 }}>
								{COVER_LABEL[row.method]} · {row.heads} head{row.heads === 1 ? "" : "s"}
								{row.spotsLeft != null && row.status !== "DRAFT" && (
									<> · {row.spotsLeft} unclaimed</>
								)}
							</div>
							<div style={{ textAlign: "right" }}>
								<span
									style={{
										display: "inline-block",
										fontFamily: lbb.fontMono,
										fontSize: 11,
										fontWeight: 700,
										letterSpacing: "0.1em",
										padding: "4px 8px",
										borderRadius: 2,
										border: lbb.borderInk,
										background: STATUS_STYLE[row.status].bg,
										color: STATUS_STYLE[row.status].fg,
									}}
								>
									{row.status}
								</span>
							</div>
						</div>
					))}

					{published && (
						<div style={{ marginTop: 20 }}>
							<button
								type="button"
								onClick={onOpenFlexScheduler}
								style={{
									fontFamily: lbb.fontUi,
									fontSize: 13,
									fontWeight: 700,
									padding: "10px 16px",
									border: lbb.borderInk,
									borderRadius: 4,
									background: lbb.paper,
									color: lbb.navy,
									cursor: "pointer",
								}}
							>
								Open in the flex scheduler ›
							</button>
						</div>
					)}
				</>
			)}
		</div>
	);
}

export default FlexRequests;
